import { WidgetPluginComponentData } from '@wix/ambassador-app-service-webapp/types';
import {
  DocumentServicesObject,
  DsItem,
  CompRef,
} from '@wix/document-services-types';
import {
  WidgetPointer,
  PluginActionContext,
  PluginsEditorType,
} from '@wix/editor-platform-host-integration-apis';
import { HttpClient } from '@wix/http-client';
import { LogObject } from '@wix/bi-logger-editor-x/v2/types';
import { PluginPlacement } from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types';
import { PluginInfo } from '../../../../types/widgetPlugins';
import { reportBi } from '../../../../essentials';
import { ComponentPreviewState } from '../constants';
import { createDependenciesDriver } from '../../appDependenciesDriver';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import { getEditorInfo } from '../../../../utils';
import { PluginActionFullContext } from '../types';

function getEditorHost(): PluginsEditorType {
  const host = getEditorInfo()?.host;
  const editorName = host?.toLowerCase();
  switch (editorName) {
    case 'editor':
      return PluginsEditorType.Classic;
    case 'editor_x':
      return PluginsEditorType.Responsive;
    case 'studio':
      return PluginsEditorType.Studio;
    case 'blocks':
      return PluginsEditorType.Blocks;
    case 'editor3':
      return PluginsEditorType.Editor3;
    default:
      throw new Error(`Unsupported editor type for plugins: ${editorName}`);
  }
}

export function extendPluginActionContext(
  context: PluginActionContext,
): PluginActionFullContext {
  const editorName = getEditorHost();
  const editorKind =
    editorName === PluginsEditorType.Blocks
      ? 'applicationEditor'
      : 'siteEditor';

  return {
    ...context,
    editorName,
    editorKind,
  };
}

export function getPluginMarketData(
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
): WidgetPluginComponentData | undefined {
  const pluginMarketDataComponent = documentServicesAPI.tpa.app
    .getDataByAppDefId(widgetPointer.appDefinitionId)
    ?.components?.find(
      (component) =>
        (component?.data as WidgetPluginComponentData)?.referenceComponentId ===
        widgetPointer.widgetId,
    );
  return pluginMarketDataComponent?.data as WidgetPluginComponentData;
}
export function getPluginInfo(
  documentServicesAPI: DocumentServicesObject,
  slotsComponentData: DsItem | undefined,
): PluginInfo | undefined {
  if (!slotsComponentData) {
    return undefined;
  }

  return {
    ...getPluginMarketData(documentServicesAPI, {
      appDefinitionId: slotsComponentData.appDefinitionId,
      widgetId: slotsComponentData.widgetId,
    })?.marketData,
    widgetId: slotsComponentData.widgetId,
    appDefinitionId: slotsComponentData.appDefinitionId,
  } as PluginInfo;
}

export function reportPluginBi(logObject: LogObject<Object>) {
  const { params, ...event } = logObject;
  reportBi(logObject.evid, {
    ...event,
    ...params,
  });
}

export function serializePlacement(placement: PluginPlacement) {
  return (
    placement &&
    `${placement.appDefinitionId}_${placement.widgetId}_${placement.slotId}`
  );
}

export function buildComponentPreviewState(
  slotName: string,
  state: ComponentPreviewState,
) {
  return `${slotName}-${state}`;
}

export function getEditorPreviewFrame(): HTMLIFrameElement {
  return (document.getElementById('preview') || // Classic Editor
    document.getElementsByName('preview-frame')[0]) as HTMLIFrameElement; // Studio Editor
}

/*
 * The functions which used `_DEPRECATED_BAD_PERFORMANCE` suffix are migrated from santa-editor repository.
 * They use deprecated API which are not recommended to use.
 *
 * Instead of:
 * - `documentServicesAPI.components.getContainer`
 * They use:
 * - `documentServicesAPI.deprecatedOldBadPerformanceApis.components.getContainer`
 *
 * The only reason for using deprecated API is to get access to components inside `RefComponent`.
 */
export function getContainer_DEPRECATED_BAD_PERFORMANCE(
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
): CompRef | null {
  return documentServicesAPI.deprecatedOldBadPerformanceApis.components.getContainer(
    compRef,
  );
}

export function isAppInstalled(
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
): boolean {
  return (
    documentServicesAPI.platform.isAppActive(widgetPointer.appDefinitionId) ||
    documentServicesAPI.tpa.app.isInstalled(widgetPointer.appDefinitionId)
  );
}

async function startAppManagerInstallation(
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
): Promise<void> {
  const { ClientSDK, Platform } = await import(
    '@wix/installation-manager-client-sdk'
  );
  const metaSiteId = documentServicesAPI.generalInfo.getMetaSiteId();
  const appInstaller = new ClientSDK({
    platform: Platform.EDITOR,
    metaSiteId,
    httpClient: new HttpClient(),
  });
  const withVersion = widgetPointer.version
    ? { version: widgetPointer.version }
    : {};
  return new Promise((resolve, reject) => {
    // opens concent modal
    appInstaller.installAppWithIntent({
      appDefId: widgetPointer.appDefinitionId,
      onInstallationSuccess: resolve,
      onInstallationFail: reject,
      onInstallationCancel: reject,
      onInstallationWindowClosed: reject,
      intent: 'plugins',
      ...withVersion,
    });
  });
}

export async function installPluginApp(
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
  actionContext: PluginActionContext,
  experimentsAPI: IExperimentsApiAdapter,
) {
  if (actionContext.controlMode === 'user') {
    await startAppManagerInstallation(documentServicesAPI, widgetPointer);
  }

  const dependencyDriver = await createDependenciesDriver(
    documentServicesAPI,
    widgetPointer.appDefinitionId,
  );
  const appDefIdsToInstall =
    dependencyDriver.getAppsToInstall().appsToInstall || [];
  for (const appDefId of appDefIdsToInstall) {
    if (experimentsAPI.enabled('se_wp_addApps')) {
      await addApp(appDefId, documentServicesAPI);
    } else {
      const options = widgetPointer.version
        ? { appVersion: widgetPointer.version }
        : {};
      await documentServicesAPI.platform.provision(appDefId, options);
    }
  }
}

function addApp(
  appDefinitionId: string,
  documentServicesAPI: DocumentServicesObject,
) {
  return new Promise<void>((resolve) => {
    documentServicesAPI.platform.addApps([appDefinitionId], {
      [appDefinitionId]: { headlessInstallation: true },
      singleAppCallback: () => {
        resolve();
      },
    });
  });
}
export {
  createWidgetPluginComponentDefinition,
  getResponsiveLayouts,
  getWidgetPluginType,
} from './pluginComponentDefinitions';
export { getSlotDefinitions, filterPublicSlots } from './slotDefinitions';
